import React, { useEffect } from 'react';
import Debug from 'debug';
import * as Sentry from '@sentry/browser';
import { getComposeItem, setBooleanCustomProperty } from '../abstractions/item';
import { isViewInComposeMode } from '../abstractions/office';
import { clearNotification } from '../abstractions/notifications';
import type { AppLoginState, IsOfficeState, UserStatus } from '../components/App';
import { isNativeSend } from '../lib/host';
import { LoginState, userService } from '../services/UserService';
import { UserPollingService } from '../services/UserPollingService';
import { defaultUserContext } from '../components/App';

const debug = Debug('o365:ui:useUserState');

type UseUserStateOptions = {
  userState: UserStatus;
  isOffice: IsOfficeState;
  setLoginMessage: React.Dispatch<React.SetStateAction<string>>;
  setUserState: React.Dispatch<React.SetStateAction<UserStatus>>;
};

/**
 * Check with the UserService to see when the user has logged in or out
 */
export function useUserState({ userState, isOffice, setLoginMessage, setUserState }: UseUserStateOptions) {
  useEffect(() => {
    const userPolling = new UserPollingService();

    function onUserStateChanged(state: LoginState) {
      debug('onUserStateChanged', state);
      let loggedin: AppLoginState = state === 'loggedin';
      if (state === 'pending' || state === 'loading') {
        loggedin = state;
      }

      updateUserContext(loggedin);

      setLoginMessage('');

      switch (state) {
        case 'loading':
        case 'pending':
          userPolling.stopPolling();
          return;
        case 'loggedin':
          userPolling.startPolling();
          if (isNativeSend() && isViewInComposeMode()) {
            const item = getComposeItem();
            clearNotification();
            setBooleanCustomProperty(item, 'SKIP_TRACKING_ERROR', null);
          }
          return;
        case 'loggedout':
          userPolling.stopPolling();
          setUserState(defaultUserContext);
          return;
      }
    }

    function updateUserContext(loggedin: AppLoginState) {
      const user = userService.getUser();
      const trial = userService.getTrial();

      // populate trial information
      let { daysLeftInTrial: daysLeft, inTrial, buyNowUrl } = userState;
      debug('updateUserContext()', {
        existingInTrial: inTrial,
        inTrial: user?.get('inTrial'),
        user,
      });
      if (trial) {
        inTrial = trial?.get('isInTrial');

        if (user?.get('inTrial') === true && inTrial) {
          debug('updateUserContext(): user is in trial');
          daysLeft = trial?.get('daysLeftInTrial') ?? -1;
          const dialog = trial?.get('dialog') ?? {};
          buyNowUrl = 'buttonUrl' in dialog ? dialog.buttonUrl : null;
        }
      }

      // populate subscriptionStatus
      let { subscriptionStatus } = userState;
      if (user?.get('subscriptionStatus')) {
        subscriptionStatus = user?.get('subscriptionStatus');
      }

      if (user) {
        Sentry.setUser({ id: user.get('clientId'), email: user.get('userName') });
      } else {
        Sentry.setUser(null);
      }

      const state = {
        ...userState,
        token: userService.getTokenObject(),
        daysLeftInTrial: daysLeft,
        user,
        loggedin,
        inTrial,
        isVideoReviewEnabled: user?.get('isVideoReviewEnabled'),
        buyNowUrl,
        subscriptionStatus,
      };
      debug('updateUserContext(): updating user state', { ...state });
      setUserState(state);
    }

    function onTrialUpdated() {
      debug('onTrialUpdated');
      updateUserContext(userState.loggedin);
    }

    function onLoginError(message: string) {
      setLoginMessage(message);
    }

    userService.addListener('state', onUserStateChanged);
    userService.addListener('trialUpdated', onTrialUpdated);
    userService.addListener('error', onLoginError);
    // Wait for listeners to be added prior to initializing UserService
    if (!userService.initializedCalled) {
      userService.initialize();
    }
    return () => {
      userService.removeListener('state', onUserStateChanged);
      userService.removeListener('trialUpdated', onTrialUpdated);
      userService.removeListener('error', onLoginError);
    };
  }, [userState, isOffice, setLoginMessage, setUserState]);
}
